/**
 * noise3d.ts — the TILEABLE 3D noise volume every fire rung samples (fire
 * realism R1, 2026-09-25).
 *
 * WHY A TEXTURE. The flame march takes 12–24 steps and needs a domain warp
 * plus a density fetch per step; Perlin ALU (`mx_noise_float`) at that count
 * is the whole cost of the shader, a trilinear 3D texture fetch is nearly
 * free. So the noise is BAKED once at load into a 64³ RGBA8 volume:
 *
 *   R, G, B  three independent periodic gradient-noise fBms (4 octaves each,
 *            lattice periods 4, 8, 16, 32 across the cube) — one fetch is a
 *            vec3 domain warp;
 *   A        a finer 2-octave fBm (periods 16, 32) — the density detail.
 *
 * Every octave's lattice period divides the cube, so the volume TILES in all
 * three axes (sampled with RepeatWrapping, a scrolling fetch never seams).
 * Values are fBm in [−1, 1] mapped to [0, 255] around 128. Deterministic:
 * the seed fixes the gradient table, so every client bakes the same bytes.
 * Pure (no three at module scope).
 */

export const NOISE3D_SIZE = 64;

/** A tiny deterministic PRNG (mulberry32). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A periodic 3D gradient-noise field (Perlin, quintic fade) with an integer lattice period. */
export class PeriodicNoise {
  private readonly perm: Uint16Array;
  private readonly grad: Float32Array;
  /** Per period: the gradient at every lattice point (p³ × 3), so the bake never hashes in its inner loop. */
  private readonly tables = new Map<number, Float32Array>();
  constructor(seed: number) {
    const r = mulberry32(seed);
    const n = 256;
    const perm = new Uint16Array(n * 2);
    const p = Array.from({ length: n }, (_, i) => i);
    for (let i = n - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [p[i], p[j]] = [p[j], p[i]]; }
    for (let i = 0; i < n * 2; i++) perm[i] = p[i & 255];
    this.perm = perm;
    // Unit gradients spread over the sphere (a random set, not the 12 cube edges — fewer axis artefacts).
    const grad = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const u = r() * 2 - 1, a = r() * Math.PI * 2, s = Math.sqrt(1 - u * u);
      grad[i * 3] = s * Math.cos(a); grad[i * 3 + 1] = u; grad[i * 3 + 2] = s * Math.sin(a);
    }
    this.grad = grad;
  }

  private table(period: number): Float32Array {
    let t = this.tables.get(period);
    if (t) return t;
    t = new Float32Array(period * period * period * 3);
    for (let z = 0; z < period; z++) for (let y = 0; y < period; y++) for (let x = 0; x < period; x++) {
      const h = this.perm[this.perm[this.perm[x & 255] + (y & 255)] + (z & 255)] * 3;
      const o = ((z * period + y) * period + x) * 3;
      t[o] = this.grad[h]; t[o + 1] = this.grad[h + 1]; t[o + 2] = this.grad[h + 2];
    }
    this.tables.set(period, t);
    return t;
  }

  /** Noise at (x, y, z) in LATTICE units, periodic with `period` cells on every axis. ≈[−1, 1]. */
  at(x: number, y: number, z: number, period: number): number {
    const T = this.table(period);
    const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
    const fx = x - x0, fy = y - y0, fz = z - z0;
    const u = fx * fx * fx * (fx * (fx * 6 - 15) + 10), v = fy * fy * fy * (fy * (fy * 6 - 15) + 10), w = fz * fz * fz * (fz * (fz * 6 - 15) + 10);
    const P = period;
    const xa = ((x0 % P) + P) % P, ya = ((y0 % P) + P) % P, za = ((z0 % P) + P) % P;
    const xb = xa + 1 === P ? 0 : xa + 1, yb = ya + 1 === P ? 0 : ya + 1, zb = za + 1 === P ? 0 : za + 1;
    const i = (ix: number, iy: number, iz: number): number => ((iz * P + iy) * P + ix) * 3;
    let o = i(xa, ya, za); const n000 = T[o] * fx + T[o + 1] * fy + T[o + 2] * fz;
    o = i(xb, ya, za); const n100 = T[o] * (fx - 1) + T[o + 1] * fy + T[o + 2] * fz;
    o = i(xa, yb, za); const n010 = T[o] * fx + T[o + 1] * (fy - 1) + T[o + 2] * fz;
    o = i(xb, yb, za); const n110 = T[o] * (fx - 1) + T[o + 1] * (fy - 1) + T[o + 2] * fz;
    o = i(xa, ya, zb); const n001 = T[o] * fx + T[o + 1] * fy + T[o + 2] * (fz - 1);
    o = i(xb, ya, zb); const n101 = T[o] * (fx - 1) + T[o + 1] * fy + T[o + 2] * (fz - 1);
    o = i(xa, yb, zb); const n011 = T[o] * fx + T[o + 1] * (fy - 1) + T[o + 2] * (fz - 1);
    o = i(xb, yb, zb); const n111 = T[o] * (fx - 1) + T[o + 1] * (fy - 1) + T[o + 2] * (fz - 1);
    const a = n000 + (n100 - n000) * u, b = n010 + (n110 - n010) * u, c = n001 + (n101 - n001) * u, d = n011 + (n111 - n011) * u;
    const e = a + (b - a) * v, f = c + (d - c) * v;
    // Gradient noise with unit gradients peaks near ±0.87 in 3D; ×1.15 brings it close to ±1.
    return 1.15 * (e + (f - e) * w);
  }
}

/**
 * Periodic fBm at a texel coordinate `t ∈ [0, size)³`: octave k has lattice
 * period `periods[k]` over the whole cube (so it tiles), amplitude 0.5^k,
 * normalised by the amplitude sum.
 */
export function periodicFbm(noise: PeriodicNoise, tx: number, ty: number, tz: number, size: number, periods: readonly number[]): number {
  let sum = 0, amp = 1, norm = 0;
  for (const p of periods) {
    const s = p / size;
    sum += amp * noise.at(tx * s, ty * s, tz * s, p);
    norm += amp;
    amp *= 0.5;
  }
  return sum / norm;
}

/**
 * The fBm's spread is narrow (σ ≈ 0.2 of its ±1 range), so the bytes stretch
 * it ×NOISE3D_GAIN around the mean before quantising: the flame reads the
 * full 8 bits instead of the middle half. Shaders read (v − 0.5) · 2 / GAIN
 * when they need the unstretched value; the fire shader remaps anyway.
 */
export const NOISE3D_GAIN = 2.2;

/** Bake the RGBA8 volume (x fastest, then y, then z — three's Data3DTexture layout). */
export function bakeNoise3D(size = NOISE3D_SIZE, seed = 0x5eed): Uint8Array {
  const out = new Uint8Array(size * size * size * 4);
  const nr = new PeriodicNoise(seed), ng = new PeriodicNoise(seed + 101), nb = new PeriodicNoise(seed + 202), na = new PeriodicNoise(seed + 303);
  const low = [4, 8, 16, 32].filter((p) => p <= size), high = [16, 32].filter((p) => p <= size);
  const q = (v: number): number => Math.max(0, Math.min(255, Math.round(127.5 + v * 127.5 * NOISE3D_GAIN)));
  let o = 0;
  for (let z = 0; z < size; z++) for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    out[o++] = q(periodicFbm(nr, x, y, z, size, low));
    out[o++] = q(periodicFbm(ng, x, y, z, size, low));
    out[o++] = q(periodicFbm(nb, x, y, z, size, low));
    out[o++] = q(periodicFbm(na, x, y, z, size, high));
  }
  return out;
}

/** Trilinear, wrapped CPU sample of the baked volume at normalised uvw (the CPU oracle's fetch). Channels 0..1. */
export function sampleNoise3D(vol: Uint8Array, size: number, u: number, v: number, w: number): [number, number, number, number] {
  const fx = u * size - 0.5, fy = v * size - 0.5, fz = w * size - 0.5;
  const x0 = Math.floor(fx), y0 = Math.floor(fy), z0 = Math.floor(fz);
  const tx = fx - x0, ty = fy - y0, tz = fz - z0;
  const wrap = (i: number): number => ((i % size) + size) % size;
  const out: [number, number, number, number] = [0, 0, 0, 0];
  for (let dz = 0; dz < 2; dz++) for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) {
    const wgt = (dx ? tx : 1 - tx) * (dy ? ty : 1 - ty) * (dz ? tz : 1 - tz);
    const o = ((wrap(z0 + dz) * size + wrap(y0 + dy)) * size + wrap(x0 + dx)) * 4;
    for (let c = 0; c < 4; c++) out[c] += wgt * vol[o + c];
  }
  for (let c = 0; c < 4; c++) out[c] /= 255;
  return out;
}
